import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button, InputField } from "../../components/ui";
import type { GtmBrainDoc, GtmBrainDocType } from "../api/GtmApi";

type Props = {
  title: string;
  docType: GtmBrainDocType;
  docs: GtmBrainDoc[];
  onChange: (docs: GtmBrainDoc[]) => void;
};

function listText(values?: string[] | null) {
  return (values || []).join(", ");
}

function parseList(text: string) {
  return text
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function emptyDoc(docType: GtmBrainDocType): GtmBrainDoc {
  return { docType };
}

export default function GtmBrainDocSection({ title, docType, docs, onChange }: Props) {
  const { t } = useTranslation();
  const items = docs.filter((d) => d.docType === docType);
  const others = docs.filter((d) => d.docType !== docType);

  const updateItem = (index: number, patch: Partial<GtmBrainDoc>) => {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange([...others, ...next]);
  };

  const removeItem = (index: number) => {
    onChange([...others, ...items.filter((_, i) => i !== index)]);
  };

  const addItem = () => {
    onChange([...others, ...items, emptyDoc(docType)]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={addItem}
        >
          {t("Add")}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">{t("None yet.")}</p>
      ) : (
        items.map((item, index) => (
          <div
            key={`${docType}-${index}`}
            className="space-y-3 border border-gray-200 rounded-lg p-3"
          >
            <div className="flex justify-end">
              <button
                type="button"
                className="text-gray-400 hover:text-red-600"
                onClick={() => removeItem(index)}
                aria-label={t("Remove")}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {docType === "persona" && (
              <>
                <InputField
                  label={t("Name")}
                  value={item.name || ""}
                  onChange={(v) => updateItem(index, { name: v })}
                />
                <InputField
                  label={t("Seniority")}
                  value={item.seniority || ""}
                  onChange={(v) => updateItem(index, { seniority: v })}
                />
                <InputField
                  mode="textarea"
                  rows={3}
                  label={t("Description")}
                  value={item.description || ""}
                  onChange={(v) => updateItem(index, { description: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Top pains (comma-separated)")}
                  value={listText(item.topPains)}
                  onChange={(v) => updateItem(index, { topPains: parseList(v) })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Watering holes (comma-separated)")}
                  value={listText(item.wateringHoles)}
                  onChange={(v) => updateItem(index, { wateringHoles: parseList(v) })}
                />
              </>
            )}
            {docType === "pillar" && (
              <>
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Pain")}
                  value={item.pain || ""}
                  onChange={(v) => updateItem(index, { pain: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Value proposition")}
                  value={item.valueProposition || ""}
                  onChange={(v) => updateItem(index, { valueProposition: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Proof")}
                  value={item.proof || ""}
                  onChange={(v) => updateItem(index, { proof: v })}
                />
              </>
            )}
            {docType === "objection" && (
              <>
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Objection")}
                  value={item.objection || ""}
                  onChange={(v) => updateItem(index, { objection: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Response")}
                  value={item.response || ""}
                  onChange={(v) => updateItem(index, { response: v })}
                />
              </>
            )}
            {docType === "competitor" && (
              <>
                <InputField
                  label={t("Name")}
                  value={item.name || ""}
                  onChange={(v) => updateItem(index, { name: v })}
                />
                <InputField
                  label={t("Domain")}
                  value={item.domain || ""}
                  onChange={(v) => updateItem(index, { domain: v })}
                />
                <InputField
                  label={t("LinkedIn URL")}
                  value={item.linkedinUrl || ""}
                  onChange={(v) => updateItem(index, { linkedinUrl: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Summary")}
                  value={item.description || ""}
                  onChange={(v) => updateItem(index, { description: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Positioning notes")}
                  value={item.notes || ""}
                  onChange={(v) => updateItem(index, { notes: v })}
                />
              </>
            )}
            {docType === "influencer" && (
              <>
                <InputField
                  label={t("Name")}
                  value={item.name || ""}
                  onChange={(v) => updateItem(index, { name: v })}
                />
                <InputField
                  label={t("Domain")}
                  value={item.domain || ""}
                  onChange={(v) => updateItem(index, { domain: v })}
                />
                <InputField
                  label={t("LinkedIn URL")}
                  value={item.linkedinUrl || ""}
                  onChange={(v) => updateItem(index, { linkedinUrl: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Summary")}
                  value={item.description || ""}
                  onChange={(v) => updateItem(index, { description: v })}
                />
                <InputField
                  mode="textarea"
                  rows={2}
                  label={t("Positioning notes")}
                  value={item.notes || ""}
                  onChange={(v) => updateItem(index, { notes: v })}
                />
              </>
            )}
            {docType === "keyword" && (
              <InputField
                label={t("Phrase")}
                value={item.phrase || ""}
                onChange={(v) => updateItem(index, { phrase: v })}
              />
            )}
          </div>
        ))
      )}
    </div>
  );
}
